import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { getStoredUser, updateStoredUser } from "../lib/auth";
import { EDUCATION_LEVEL_OPTIONS, GENDER_OPTIONS, GOVERNORATE_OPTIONS } from "../lib/formOptions";
import { useLanguage } from "../lib/language";
import { getProfileImageStyle } from "../lib/profileImage";
import { getPreferredDisplayName, hasMeaningfulText, repairMojibakeDeep, repairMojibakeText } from "../lib/text";

const emptyProfile = {
  full_name: "",
  email: "",
  role: "",
  age: "",
  birth_date: "",
  national_id: "",
  governorate: "",
  phone_number: "",
  gender: "",
  education_level: "",
  institution_name: "",
  profile_image_url: "",
  profile_image_focus_x: 50,
  profile_image_focus_y: 50
};

const copyMap = {
  ar: {
    eyebrow: "الملف الشخصي",
    title: "بيانات الحساب",
    subtitle: "حدّث بياناتك الأساسية من مكان واحد مع الحفاظ على القيم الموحدة المستخدمة وقت التسجيل.",
    name: "الاسم الكامل",
    email: "البريد الإلكتروني",
    phone: "رقم الهاتف",
    birthDate: "تاريخ الميلاد",
    nationalId: "الرقم القومي",
    governorate: "المحافظة",
    education: "المرحلة التعليمية",
    institution: "اسم الجامعة أو المدرسة أو جهة العمل",
    gender: "النوع",
    age: "العمر",
    image: "رابط الصورة الشخصية",
    imageHint: "يمكنك وضع رابط صورة مباشر أو رابط عام للصورة.",
    uploadImage: "رفع صورة من الجهاز",
    uploadHint: "يمكنك الرفع مباشرة داخل المنصة بدل الاعتماد على روابط خارجية.",
    uploadingImage: "جارٍ رفع الصورة...",
    role: "الدور",
    save: "حفظ التعديلات",
    saving: "جارٍ الحفظ...",
    success: "تم تحديث الملف الشخصي بنجاح",
    loadError: "تعذر تحميل الملف الشخصي",
    saveError: "تعذر حفظ الملف الشخصي",
    student: "طالب",
    trainer: "مدرب",
    admin: "مدير النظام",
    adjustImage: "ضبط موضع الصورة",
    adjustImageHint: "حرّك الصورة حتى يبقى الوجه أو العنصر المهم في المنتصف.",
    horizontalFocus: "التمركز الأفقي",
    verticalFocus: "التمركز الرأسي"
  },
  en: {
    eyebrow: "Profile",
    title: "Account Profile",
    subtitle: "Update your key account details from one place while keeping registration values consistent.",
    name: "Full Name",
    email: "Email",
    phone: "Phone Number",
    birthDate: "Date of Birth",
    nationalId: "National ID",
    governorate: "Governorate",
    education: "Education Level",
    institution: "University, School, or Workplace",
    gender: "Gender",
    age: "Age",
    image: "Profile Image URL",
    imageHint: "You can use a public image link or any direct image URL.",
    uploadImage: "Upload image from device",
    uploadHint: "You can upload directly to the platform instead of relying on external links.",
    uploadingImage: "Uploading image...",
    role: "Role",
    save: "Save Changes",
    saving: "Saving...",
    success: "Profile updated successfully",
    loadError: "Could not load the profile",
    saveError: "Could not save the profile",
    student: "Student",
    trainer: "Trainer",
    admin: "Admin",
    adjustImage: "Adjust Image Focus",
    adjustImageHint: "Move the image so the face or main subject stays centered.",
    horizontalFocus: "Horizontal Focus",
    verticalFocus: "Vertical Focus"
  }
};

function ProfilePage() {
  const user = getStoredUser();
  const { language } = useLanguage();
  const isArabic = language === "ar";
  const copy = repairMojibakeDeep(copyMap[isArabic ? "ar" : "en"]);

  const [profile, setProfile] = useState(emptyProfile);
  const [feedback, setFeedback] = useState("");
  const [feedbackType, setFeedbackType] = useState("info");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function fetchProfile() {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.get("/account/profile", {
          params: { user_id: user.id }
        });

        if (!isMounted) {
          return;
        }

        const rawProfile = response.data.profile || {};
        const repairedProfile = repairMojibakeDeep({
          ...emptyProfile,
          ...rawProfile
        });
        setProfile({
          ...repairedProfile,
          governorate: hasMeaningfulText(rawProfile.governorate || "") ? rawProfile.governorate : "",
          education_level: hasMeaningfulText(rawProfile.education_level || "") ? rawProfile.education_level : "",
          gender: rawProfile.gender || ""
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setFeedbackType("error");
        setFeedback(repairMojibakeText(error.response?.data?.error || copy.loadError));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchProfile();
    return () => {
      isMounted = false;
    };
  }, [copy.loadError, user?.id]);

  const roleLabel = profile.role === "admin"
    ? copy.admin
    : profile.role === "trainer"
      ? copy.trainer
      : copy.student;

  const displayName = getPreferredDisplayName(
    { ...user, full_name: profile.full_name || user?.full_name, email: profile.email || user?.email },
    isArabic ? "المستخدم" : "User"
  );

  const avatarLabel = displayName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();

  const governorateOptions = useMemo(
    () => GOVERNORATE_OPTIONS.map((option) => ({
      ...option,
      labelAr: repairMojibakeText(option.labelAr)
    })),
    []
  );

  const educationOptions = useMemo(
    () => EDUCATION_LEVEL_OPTIONS.map((option) => ({
      ...option,
      labelAr: repairMojibakeText(option.labelAr)
    })),
    []
  );

  const genderOptions = useMemo(
    () => GENDER_OPTIONS.map((option) => ({
      ...option,
      labelAr: repairMojibakeText(option.labelAr)
    })),
    []
  );

  const handleChange = (field, value) => {
    setProfile((current) => {
      const nextProfile = { ...current, [field]: value };

      if (field === "birth_date" && value) {
        const today = new Date();
        const birthDate = new Date(value);
        let nextAge = today.getFullYear() - birthDate.getFullYear();
        const monthDelta = today.getMonth() - birthDate.getMonth();
        if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birthDate.getDate())) {
          nextAge -= 1;
        }
        nextProfile.age = nextAge >= 0 ? String(nextAge) : "";
      }

      if (field === "national_id") {
        nextProfile.national_id = String(value).replace(/\D/g, "").slice(0, 14);
      }

      return nextProfile;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setIsSaving(true);
      const response = await api.put("/account/profile", {
        user_id: user?.id,
        ...profile,
        governorate: hasMeaningfulText(profile.governorate || "") ? profile.governorate : null,
        education_level: hasMeaningfulText(profile.education_level || "") ? profile.education_level : null
      });

      const rawProfile = response.data.profile || profile;
      const nextProfile = repairMojibakeDeep(rawProfile);
      const nextState = {
        ...nextProfile,
        governorate: hasMeaningfulText(rawProfile.governorate || "") ? rawProfile.governorate : "",
        education_level: hasMeaningfulText(rawProfile.education_level || "") ? rawProfile.education_level : "",
        gender: rawProfile.gender || ""
      };
      setProfile((current) => ({ ...current, ...nextState }));
      updateStoredUser({
        ...nextState,
        full_name: getPreferredDisplayName(
          { ...user, ...nextState, email: nextState.email || user?.email },
          nextState.full_name || user?.full_name || "User"
        )
      });
      setFeedbackType("success");
      setFeedback(copy.success);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(repairMojibakeText(error.response?.data?.error || copy.saveError));
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) {
      return;
    }

    try {
      setIsUploadingImage(true);
      const formData = new FormData();
      formData.append("user_id", String(user.id));
      formData.append("image", file);

      const response = await api.post("/account/profile-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      const nextUrl = repairMojibakeText(response.data?.profile_image_url || "");
      const nextFocusX = response.data?.profile_image_focus_x ?? 50;
      const nextFocusY = response.data?.profile_image_focus_y ?? 50;
      setProfile((current) => ({
        ...current,
        profile_image_url: nextUrl,
        profile_image_focus_x: nextFocusX,
        profile_image_focus_y: nextFocusY
      }));
      updateStoredUser({
        profile_image_url: nextUrl,
        profile_image_focus_x: nextFocusX,
        profile_image_focus_y: nextFocusY
      });
      setFeedbackType("success");
      setFeedback(copy.success);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(repairMojibakeText(error.response?.data?.error || copy.saveError));
    } finally {
      setIsUploadingImage(false);
      event.target.value = "";
    }
  };

  return (
    <div className="workspace-page">
      <section className="hero-panel hero-panel--profile">
        <div>
          <p className="section-eyebrow">{copy.eyebrow}</p>
          <h2 className="section-title">{copy.title}</h2>
          <p className="section-subtitle">{copy.subtitle}</p>
        </div>
        <div className="profile-hero-card">
          {profile.profile_image_url ? (
            <img
              src={profile.profile_image_url}
              alt={displayName}
              className="profile-avatar"
              style={getProfileImageStyle(profile)}
            />
          ) : (
            <div className="profile-avatar profile-avatar--fallback">{avatarLabel || "U"}</div>
          )}
          <div className="stack-sm">
            <strong>{displayName}</strong>
            <span className="muted-text">{roleLabel}</span>
          </div>
        </div>
      </section>

      <section className="workspace-grid workspace-grid--profile">
        <div className="panel-card">
          <form className="stack-lg" onSubmit={handleSubmit}>
            <div className="two-column-grid">
              <label className="field-block">
                <span>{copy.name}</span>
                <input
                  className="text-input"
                  value={profile.full_name || ""}
                  onChange={(event) => handleChange("full_name", event.target.value)}
                />
              </label>
              <label className="field-block">
                <span>{copy.email}</span>
                <input className="text-input" value={profile.email || ""} disabled />
              </label>
              <label className="field-block">
                <span>{copy.phone}</span>
                <input
                  className="text-input"
                  value={profile.phone_number || ""}
                  onChange={(event) => handleChange("phone_number", event.target.value)}
                />
              </label>
              <label className="field-block">
                <span>{copy.birthDate}</span>
                <input
                  className="text-input"
                  type="date"
                  value={profile.birth_date || ""}
                  onChange={(event) => handleChange("birth_date", event.target.value)}
                />
              </label>
              <label className="field-block">
                <span>{copy.governorate}</span>
                <select
                  className="text-input"
                  value={profile.governorate || ""}
                  onChange={(event) => handleChange("governorate", event.target.value)}
                >
                  <option value="">-</option>
                  {governorateOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {isArabic ? option.labelAr : option.labelEn}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field-block">
                <span>{copy.education}</span>
                <select
                  className="text-input"
                  value={profile.education_level || ""}
                  onChange={(event) => handleChange("education_level", event.target.value)}
                >
                  <option value="">-</option>
                  {educationOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {isArabic ? option.labelAr : option.labelEn}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field-block">
                <span>{copy.age}</span>
                <input
                  className="text-input"
                  type="number"
                  value={profile.age || ""}
                  onChange={(event) => handleChange("age", event.target.value)}
                />
              </label>
              <label className="field-block">
                <span>{copy.nationalId}</span>
                <input
                  className="text-input"
                  inputMode="numeric"
                  maxLength={14}
                  value={profile.national_id || ""}
                  onChange={(event) => handleChange("national_id", event.target.value)}
                />
              </label>
              <label className="field-block">
                <span>{copy.institution}</span>
                <input
                  className="text-input"
                  value={profile.institution_name || ""}
                  onChange={(event) => handleChange("institution_name", event.target.value)}
                />
              </label>
              <label className="field-block">
                <span>{copy.gender}</span>
                <select
                  className="text-input"
                  value={profile.gender || ""}
                  onChange={(event) => handleChange("gender", event.target.value)}
                >
                  <option value="">-</option>
                  {genderOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {isArabic ? option.labelAr : option.labelEn}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field-block">
                <span>{copy.role}</span>
                <input className="text-input" value={roleLabel} disabled />
              </label>
            </div>

            <label className="field-block">
              <span>{copy.image}</span>
              <input
                className="text-input"
                value={profile.profile_image_url || ""}
                onChange={(event) => handleChange("profile_image_url", event.target.value)}
                placeholder="https://..."
              />
              <small className="muted-text">{copy.imageHint}</small>
            </label>

            <label className="field-block">
              <span>{copy.uploadImage}</span>
              <input
                className="text-input"
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                onChange={handleImageUpload}
                disabled={isUploadingImage}
              />
              <small className="muted-text">
                {isUploadingImage ? copy.uploadingImage : copy.uploadHint}
              </small>
            </label>

            {profile.profile_image_url && (
              <div className="panel-card profile-image-adjuster">
                <div className="card-title-row">
                  <div>
                    <h3>{copy.adjustImage}</h3>
                    <p className="muted-text">{copy.adjustImageHint}</p>
                  </div>
                </div>

                <div className="profile-image-adjuster__preview">
                  <img
                    src={profile.profile_image_url}
                    alt={profile.full_name || "Profile"}
                    className="profile-avatar profile-avatar--editor"
                    style={getProfileImageStyle(profile)}
                  />
                </div>

                <div className="two-column-grid">
                  <label className="field-block">
                    <span>{copy.horizontalFocus}</span>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={profile.profile_image_focus_x ?? 50}
                      onChange={(event) => handleChange("profile_image_focus_x", event.target.value)}
                    />
                  </label>

                  <label className="field-block">
                    <span>{copy.verticalFocus}</span>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={profile.profile_image_focus_y ?? 50}
                      onChange={(event) => handleChange("profile_image_focus_y", event.target.value)}
                    />
                  </label>
                </div>
              </div>
            )}

            {feedback && <div className={`feedback-banner feedback-banner--${feedbackType}`}>{feedback}</div>}

            <div className="modal-actions">
              <button className="primary-button" type="submit" disabled={isSaving || isLoading}>
                {isSaving ? copy.saving : copy.save}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

export default ProfilePage;
